import React, { useState } from 'react';

/**
 * Calendar Component - Shows a month view calendar with available dates highlighted
 *
 * @param {Object} props
 * @param {Object} props.availabilityData - Object with date strings as keys, arrays of slots as values
 * @param {Date} props.selectedDate - Currently selected date
 * @param {Function} props.onDateSelect - Callback when a date is selected
 * @param {Date} props.currentMonth - Current month being displayed
 * @param {Function} props.onMonthChange - Callback to change month
 * @param {Object} props.translations - Translation strings for calendar
 */
const Calendar = ({
  availabilityData,
  selectedDate,
  onDateSelect,
  currentMonth,
  onMonthChange,
  translations = {}
}) => {
  // Get days in month
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    return { daysInMonth, startingDayOfWeek, firstDay };
  };

  const { daysInMonth, startingDayOfWeek } = getDaysInMonth(currentMonth);

  // Generate calendar grid
  const generateCalendarDays = () => {
    const days = [];

    // Add empty cells for days before month starts
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }

    // Add days of month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(day);
    }

    return days;
  };

  const calendarDays = generateCalendarDays();

  // Check if a date has available slots
  const hasAvailableSlots = (day) => {
    if (!day) return false;

    const date = new Date(
      currentMonth.getFullYear(),
      currentMonth.getMonth(),
      day
    );
    // Format date without timezone conversion to match API response keys
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const dayStr = String(date.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${dayStr}`;

    return availabilityData[dateStr] && availabilityData[dateStr].length > 0;
  };

  // Check if date is selected
  const isSelected = (day) => {
    if (!day || !selectedDate) return false;

    const date = new Date(
      currentMonth.getFullYear(),
      currentMonth.getMonth(),
      day
    );

    return (
      date.getDate() === selectedDate.getDate() &&
      date.getMonth() === selectedDate.getMonth() &&
      date.getFullYear() === selectedDate.getFullYear()
    );
  };

  // Check if date is today
  const isToday = (day) => {
    if (!day) return false;

    const today = new Date();
    const date = new Date(
      currentMonth.getFullYear(),
      currentMonth.getMonth(),
      day
    );

    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  };

  // Check if date is in the past
  const isPast = (day) => {
    if (!day) return false;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const date = new Date(
      currentMonth.getFullYear(),
      currentMonth.getMonth(),
      day
    );
    date.setHours(0, 0, 0, 0);

    return date < today;
  };

  // Handle date click
  const handleDateClick = (day) => {
    if (!day || isPast(day) || !hasAvailableSlots(day)) return;

    const date = new Date(
      currentMonth.getFullYear(),
      currentMonth.getMonth(),
      day
    );

    onDateSelect(date);
  };

  // Navigate to previous month
  const handlePrevMonth = () => {
    const newMonth = new Date(currentMonth);
    newMonth.setMonth(newMonth.getMonth() - 1);
    onMonthChange(newMonth);
  };

  // Navigate to next month
  const handleNextMonth = () => {
    const newMonth = new Date(currentMonth);
    newMonth.setMonth(newMonth.getMonth() + 1);
    onMonthChange(newMonth);
  };

  // Format month and year for header
  const locale = document.documentElement.lang || 'zh-TW';
  const monthYearText = currentMonth.toLocaleDateString(locale, {
    year: 'numeric',
    month: 'long'
  });

  return (
    <div className="bg-white rounded-lg shadow p-6">
      {/* Calendar Header */}
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={handlePrevMonth}
          className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          aria-label="Previous month"
        >
          <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>

        <h2 className="text-lg font-semibold text-gray-900">
          {monthYearText}
        </h2>

        <button
          onClick={handleNextMonth}
          className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          aria-label="Next month"
        >
          <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>

      {/* Weekday Headers */}
      <div className="grid grid-cols-7 gap-1 mb-2">
        {[0, 1, 2, 3, 4, 5, 6].map((dayIndex) => {
          // Get localized weekday name (short format)
          const date = new Date(2025, 0, dayIndex + 5); // Jan 5-11, 2025 starts on Sunday
          const dayName = date.toLocaleDateString(locale, { weekday: 'short' });
          return (
            <div
              key={dayIndex}
              className="text-center text-sm font-medium text-gray-500 py-2"
            >
              {dayName}
            </div>
          );
        })}
      </div>

      {/* Calendar Grid */}
      <div className="grid grid-cols-7 gap-1">
        {calendarDays.map((day, index) => {
          const hasSlots = hasAvailableSlots(day);
          const selected = isSelected(day);
          const today = isToday(day);
          const past = isPast(day);

          return (
            <button
              key={index}
              onClick={() => handleDateClick(day)}
              disabled={!day || past || !hasSlots}
              className={`
                aspect-square p-2 text-sm rounded-lg transition-all
                ${!day ? 'invisible' : ''}
                ${past ? 'text-gray-300 cursor-not-allowed' : ''}
                ${hasSlots && !past ? 'cursor-pointer hover:bg-blue-50' : ''}
                ${!hasSlots && !past && day ? 'text-gray-400 cursor-not-allowed' : ''}
                ${selected ? 'bg-[#3366FF] text-white hover:bg-[#2952CC]' : ''}
                ${today && !selected ? 'border-2 border-[#3366FF] text-[#3366FF] font-semibold' : ''}
                ${hasSlots && !selected && !today && !past ? 'bg-blue-50 text-gray-900 font-medium' : ''}
              `}
            >
              {day}
            </button>
          );
        })}
      </div>

      {/* Legend */}
      <div className="mt-4 pt-4 border-t border-gray-200">
        <div className="flex flex-wrap gap-4 text-xs text-gray-600">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-blue-50 border border-blue-200"></div>
            <span>{translations.available_dates || 'Available'}</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-[#3366FF]"></div>
            <span>{translations.selected || 'Selected'}</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded border-2 border-[#3366FF]"></div>
            <span>{translations.today || 'Today'}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Calendar;
